import { Capacitor } from "@capacitor/core";
import { useEffect } from "react";
import { scrollerOf } from "@/lib/scroll-host";

const KEYBOARD_HEIGHT_VAR = "--keyboard-height";

/** Gap kept between a focused field's bottom edge and the keyboard's top. */
const FOCUS_CLEARANCE_PX = 16;

/** How much of the window the software keyboard covers; 0 on the web. */
let coveredByKeyboard = 0;

/** How much of the window's bottom the software keyboard covers right now. */
export function softKeyboardHeight(): number {
  return coveredByKeyboard;
}

/**
 * Scrolls the focused field clear of the keyboard. Where the keyboard covers
 * the window, the layout viewport still spans the full screen, so the WebView
 * considers a field behind the keyboard "visible" and never scrolls it itself;
 * the keyboard-aware bottom insets (GlassScrollView, AuthScreen, FormSheet, the
 * drawers) provide the scroll range, and this provides the scroll.
 */
async function scrollFocusedFieldClear(): Promise<void> {
  const el = document.activeElement;
  if (
    !(
      el instanceof HTMLElement &&
      el.matches("input, textarea, select, [contenteditable]")
    )
  ) {
    return;
  }
  // Next frame, so the just-updated inset padding is in the layout before the
  // scroll range is consumed.
  await new Promise(requestAnimationFrame);
  const visibleBottom = window.innerHeight - coveredByKeyboard;
  const overlap =
    el.getBoundingClientRect().bottom - (visibleBottom - FOCUS_CLEARANCE_PX);
  if (overlap <= 0) {
    return;
  }
  const scroller = await scrollerOf(el);
  scroller?.scrollBy({ behavior: "smooth", top: overlap });
}

/**
 * Mirrors how much of the window the software keyboard covers into a
 * `--keyboard-height` CSS variable on <html>, so bottom-docked UI (the message
 * composers, pinned form action bars) can reserve space above the keyboard
 * instead of being hidden behind it, and scrolls the focused field into the
 * visible strip (see scrollFocusedFieldClear — on keyboard open, once it is up,
 * and when focus moves between fields while it stays open).
 *
 * On iOS, `Keyboard.resize` is "none" (capacitor.config.ts), so the WebView
 * keeps the full screen and the keyboard covers its bottom: the variable is the
 * keyboard's height. On Android, Capacitor's SystemBars pads the WebView above
 * the keyboard on every version, so the keyboard covers none of the window and
 * the WebView scrolls the focused field into view itself: the variable is 0.
 * https://github.com/ionic-team/capacitor/blob/8.5.3/android/capacitor/src/main/java/com/getcapacitor/plugin/SystemBars.java
 * `keyboardWillShow` fires before the slide-up animation, so
 * the reserved space appears in step with the keyboard. On the web no
 * listeners fire and the var stays 0, so browser layout is untouched.
 * Call once, high in the tree.
 */
export function useKeyboardInset(): void {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    const root = document.documentElement;
    let keyboardOpen = false;
    const keyboardCoversWindow = Capacitor.getPlatform() === "ios";
    const handles: Array<{ remove: () => void }> = [];
    // The plugin import is async, so cleanup can run before the listeners are
    // registered. `cancelled` lets the late registration tear itself down
    // instead of leaking listeners and a stale `--keyboard-height`.
    let cancelled = false;

    // Focus can move between fields while the keyboard stays open, without any
    // further keyboardWillShow; the new field may sit behind the keyboard.
    const handleFocusIn = () => {
      if (keyboardOpen) {
        void scrollFocusedFieldClear();
      }
    };
    document.addEventListener("focusin", handleFocusIn);

    void (async () => {
      const { Keyboard } = await import("@capacitor/keyboard");
      const registered = [
        await Keyboard.addListener("keyboardWillShow", ({ keyboardHeight }) => {
          keyboardOpen = true;
          coveredByKeyboard = keyboardCoversWindow ? keyboardHeight : 0;
          root.style.setProperty(KEYBOARD_HEIGHT_VAR, `${coveredByKeyboard}px`);
          // A WebView that shrinks for the keyboard is scrolling the field
          // into view as this fires; a second scroll now would add to its own.
          if (keyboardCoversWindow) {
            void scrollFocusedFieldClear();
          }
        }),
        // WebKit can drop the scroll above for a field focused by script (a
        // new checklist step), and a shrunk WebView's own scroll can leave the
        // field under the clearance, so check again once the keyboard is up.
        await Keyboard.addListener("keyboardDidShow", () => {
          void scrollFocusedFieldClear();
        }),
        await Keyboard.addListener("keyboardWillHide", () => {
          keyboardOpen = false;
          coveredByKeyboard = 0;
          root.style.setProperty(KEYBOARD_HEIGHT_VAR, "0px");
        }),
      ];
      if (cancelled) {
        for (const h of registered) {
          h.remove();
        }
        root.style.removeProperty(KEYBOARD_HEIGHT_VAR);
        return;
      }
      handles.push(...registered);
    })();

    return () => {
      cancelled = true;
      document.removeEventListener("focusin", handleFocusIn);
      for (const h of handles) {
        h.remove();
      }
      coveredByKeyboard = 0;
      root.style.removeProperty(KEYBOARD_HEIGHT_VAR);
    };
  }, []);
}
